import React, { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { getInstallmentDetail } from 'api/requests'

import Can from 'components/Can'
import IconContainer from 'components/IconContainer/IconContainer'
import ModalChargeDetail from 'components/Modal/ModalChargeDetail'
import CancelConfirmationModal from 'components/Modal/ModalChargeDetail/components/CancelConfirmationDialog/CancelConfirmationDialog'
import {
  headerGridMobileSx,
  mobile,
  modalHeaderMobileSx
} from 'components/Modal/modalHeaderMobile.styles'
import ModalRecurrenceDetail from 'components/Modal/ModalRecurrenceDetail'
import { getStatusComplete } from 'components/Status/Status.styles'
import { Tag } from 'components/TagsAddInput/TagsAddInput'
import TagsPopover from 'components/TagsPopover'

import { convertTagsStringToArray } from 'utils/tags'

import { theme } from 'styles/theme'

import Composition from './components/Composition'
import InstallmentDetails from './components/InstallmentDetails'
import InstallmentsAccordion from './components/InstallmentsAccordion'
import Loading from './components/Loading'
import NFe from './components/NFe'
import { ModalInstallmentDetailContext, InstallmentData } from './context'

type TagForm = {
  tags: Tag[]
}

type ModalInstallmentDetailBodyProps = {
  uuid: string
  onClose: (reloadTable?: boolean) => void
  setReloadTable?: React.Dispatch<React.SetStateAction<boolean>>
}

const ModalInstallmentDetailBody = ({
  uuid,
  onClose,
  setReloadTable
}: ModalInstallmentDetailBodyProps) => {
  const { installment, updateInstallment, loading, setLoading } = useContext(
    ModalInstallmentDetailContext
  )

  const { control, setValue } = useForm<TagForm>({
    defaultValues: { tags: [] }
  })

  const [chargeDetailModal, setChargeDetailModal] = useState({
    isOpen: false,
    uuid: ''
  })

  const [recurrenceDetailModal, setRecurrenceDetailModal] = useState({
    isOpen: false,
    uuid: ''
  })

  const [cancelConfirmationModal, setCancelConfirmationModal] = useState({
    isOpen: false
  })

  const copyUuid = () => {
    navigator.clipboard.writeText(uuid)
    toast.success('UUID copiado para a área de transferência!')
  }

  const handleOpenChargeDetail = (billingUuid: string) => {
    setChargeDetailModal({
      isOpen: true,
      uuid: billingUuid
    })
  }

  const handleCloseChargeDetail = (reloadTable?: boolean) => {
    setChargeDetailModal({
      isOpen: false,
      uuid: ''
    })
    if (reloadTable) {
      // Recarrega os dados do parcelamento quando uma parcela foi atualizada
      getInstallment(uuid)
      if (setReloadTable) {
        setReloadTable(true)
      }
    }
  }

  const handleOpenRecurrenceDetail = (recurrenceUuid: string) => {
    setRecurrenceDetailModal({
      isOpen: true,
      uuid: recurrenceUuid
    })
  }

  const handleCloseRecurrenceDetail = (reloadTable?: boolean) => {
    setRecurrenceDetailModal({
      isOpen: false,
      uuid: ''
    })
    if (reloadTable && setReloadTable) {
      setReloadTable(true)
    }
  }

  const handleOpenCancelConfirmationModal = () => {
    setCancelConfirmationModal({
      isOpen: true
    })
  }

  const handleCloseCancelConfirmationModal = () => {
    setCancelConfirmationModal({
      isOpen: false
    })
  }

  const getRecurrenceInfo = () => {
    if (!installment.billings || installment.billings.length === 0) return null

    const billingWithRecurrence = installment.billings.find(
      (billing) => billing.recurrence_uuid
    )

    if (!billingWithRecurrence) return null

    return {
      uuid: billingWithRecurrence.recurrence_uuid || '',
      cycle: billingWithRecurrence.recurrence_cycle || 0,
      totalCycles: billingWithRecurrence.recurrence?.total_cycles || 0
    }
  }

  const getInstallment = async (installmentUuid: string) => {
    try {
      setLoading(true)
      const response = await getInstallmentDetail({ uuid: installmentUuid })

      if (response.error) {
        toast.error('Erro ao carregar o parcelamento.')
        return
      }

      updateInstallment(response.data.record as InstallmentData)
    } catch (err) {
      toast.error('Erro ao carregar o parcelamento.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (uuid) {
      getInstallment(uuid)
    }
  }, [uuid])

  useEffect(() => {
    const rawTags: any = (installment as any)?.tags
    const tagsArray = convertTagsStringToArray(rawTags, [])
    setValue('tags', tagsArray)
  }, [installment, setValue])

  if (loading) {
    return <Loading />
  }

  return (
    <>
      <ModalBody>
        <ModalHeader onClose={onClose} sx={modalHeaderMobileSx}>
          {/* SC-11275: no celular o botão e o status empurravam o X para fora
              da tela. Mesmo header do detalhe da cobrança, com o "Acessar
              recorrência" numa linha própria no lugar do "Mais opções". */}
          <Stack
            sx={{
              cursor: 'pointer',
              [mobile]: {
                ...headerGridMobileSx[mobile],
                gridTemplateColumns: '1fr 24px',
                gridTemplateAreas: '"title ." "status status" "action action"'
              }
            }}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
            mr={2}
          >
            <Tooltip
              title={`Identificador: ${installment.uuid}`}
              placement="top"
              onClick={copyUuid}
              arrow
              PopperProps={{
                onClick: copyUuid,
                sx: { cursor: 'pointer' },
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [40, 0]
                    }
                  }
                ]
              }}
            >
              <Box sx={{ [mobile]: { gridArea: 'title', minWidth: 0 } }}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <IconContainer icon="faCreditCard" />
                  <Typography variant="mdBold">
                    Detalhes do parcelamento
                  </Typography>
                </Stack>
              </Box>
            </Tooltip>
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ [mobile]: { display: 'contents' } }}
            >
              {getRecurrenceInfo() && (
                <Button
                  variantButton="neutral"
                  sx={{ [mobile]: { gridArea: 'action', width: '100%' } }}
                  onClick={() => {
                    const recurrenceInfo = getRecurrenceInfo()
                    if (recurrenceInfo) {
                      handleOpenRecurrenceDetail(recurrenceInfo.uuid)
                    }
                  }}
                >
                  Acessar recorrência
                </Button>
              )}
              <Chip
                key={installment?.status}
                label={
                  getStatusComplete(installment?.status || '', undefined, true)
                    ?.label
                }
                sx={{
                  backgroundColor: getStatusComplete(
                    installment?.status || '',
                    undefined,
                    true
                  )?.color,
                  color: getStatusComplete(
                    installment?.status || '',
                    undefined,
                    true
                  )?.fontColor,
                  height: '25px',
                  width: '160px',
                  fontWeight: '600',
                  [theme.breakpoints.down(508)]: {
                    width: '120px'
                  },
                  [mobile]: { gridArea: 'status', width: '100%' }
                }}
              />
            </Stack>
          </Stack>
        </ModalHeader>

        <ModalContent>
          <Stack gap={2} mt={2}>
            <InstallmentDetails />
            <Composition />
            <InstallmentsAccordion
              onOpenChargeDetail={handleOpenChargeDetail}
            />
            <NFe />
          </Stack>
        </ModalContent>

        <ModalFooter>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
            width="100%"
          >
            <TagsPopover
              control={control}
              name="tags"
              saveMode="immediate"
              saveContext={{
                taggableType: 'Installment',
                taggableUuid: installment?.uuid || ''
              }}
              onSave={async () => {
                toast.success('Tags atualizadas com sucesso!')
                await getInstallment(uuid)
                setReloadTable && setReloadTable(true)
              }}
              maxTags={10}
            />
            {/* billings#installment_cancel exige billings:cancel. */}
            {installment?.status !== 'cancelled' &&
              installment?.status !== 'finished' && (
                <Can permission="billings:cancel">
                  <Button
                    variantButton="neutral"
                    onClick={handleOpenCancelConfirmationModal}
                  >
                    Cancelar parcelamento
                  </Button>
                </Can>
              )}
          </Stack>
        </ModalFooter>
      </ModalBody>

      {chargeDetailModal.isOpen && (
        <ModalChargeDetail
          uuid={chargeDetailModal.uuid}
          handleCloseChargeDetail={handleCloseChargeDetail}
          isOpenChargeDetail={chargeDetailModal.isOpen}
          setReloadTable={setReloadTable}
        />
      )}

      {recurrenceDetailModal.isOpen && (
        <ModalRecurrenceDetail
          uuid={recurrenceDetailModal.uuid}
          handleCloseDetailRecurrence={handleCloseRecurrenceDetail}
          isOpenModalRecurrenceDetail={recurrenceDetailModal.isOpen}
          reloadTable={setReloadTable}
        />
      )}

      <CancelConfirmationModal
        isOpen={cancelConfirmationModal.isOpen}
        onClose={handleCloseCancelConfirmationModal}
        billingUuid={uuid}
        onSuccess={() => {
          getInstallment(uuid)
          setReloadTable && setReloadTable((s) => !s)
        }}
        type="installment"
      />
    </>
  )
}

export default ModalInstallmentDetailBody
